import apiV2 from '../api/v2';

// Accounts > Practice detail > Users tab: paginated/searched the same way as
// the global GET /users list, but every row here is the FULL raw DB row
// (lastSeenRelease included) - see services/users.ts for the other shape.

export interface PracticeUserRow {
    id: string;
    email: string;
    fullname: string;
    isAdmin: boolean;
    practices: string[];
    createdAt: string;
    lastActivityAt: string | null;
    lastSeenRelease: string | null;
}

export interface InvitePracticeUserInput {
    email: string;
    fullname: string;
}

export interface PracticeUsersListParams {
    page: number;
    limit: number;
    search?: string;
}

export interface PracticeUsersListMeta {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface PracticeUsersListResult {
    users: PracticeUserRow[];
    meta: PracticeUsersListMeta;
}

/** Backend-enforced ceiling on `limit` - the largest single fetch this endpoint allows. */
export const PRACTICE_USERS_MAX_LIMIT = 100;

/** Backend-enforced ceiling on `search`. */
export const PRACTICE_USERS_SEARCH_MAX_LENGTH = 200;

export async function fetchPracticeUsers(
    practiceId: string,
    params: PracticeUsersListParams
): Promise<PracticeUsersListResult> {
    const response = await apiV2.get<{
        success: true;
        data: PracticeUserRow[];
        meta: PracticeUsersListMeta;
    }>(`/practices/${practiceId}/users`, {
        params: { page: params.page, limit: params.limit, search: params.search || undefined }
    });
    return { users: response.data.data, meta: response.data.meta };
}

/**
 * Single call that creates the WordPress account (tolerating "already
 * exists"), links the user to this practice (creating the DB user if it
 * doesn't exist yet, or appending the practice id if it does), and sends a
 * password-reset email - all server-side (see practices-users.service.ts's
 * addUserToPractice). No client-side WP orchestration needed here, unlike
 * the global Accounts > Users create flow.
 */
export async function invitePracticeUser(
    practiceId: string,
    input: InvitePracticeUserInput
): Promise<PracticeUserRow> {
    const response = await apiV2.post<{ success: true; data: PracticeUserRow }>(
        `/practices/${practiceId}/users`,
        input
    );
    return response.data.data;
}

/** Unlinks the user from this practice - never deletes the DB user or WP account. */
export async function removePracticeUser(practiceId: string, email: string): Promise<void> {
    await apiV2.delete(`/practices/${practiceId}/users`, { data: { email } });
}
